export interface CourtHirePrefill {
  courtId: string;
  date: string;
  startTime: string;
  durationMinutes: number;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}$/;

export function durationMinutesFromSlot(startTime: string, endTime: string): number {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return 60;
  const minutes = eh * 60 + em - (sh * 60 + sm);
  return minutes > 0 ? minutes : 60;
}

export function parseCourtHireSearchParams(params: URLSearchParams): CourtHirePrefill | null {
  const courtId = params.get('court');
  const date = params.get('date');
  const startTime = params.get('start');
  const endTime = params.get('end');

  if (!courtId || !date || !startTime) return null;
  if (!DATE_RE.test(date) || !TIME_RE.test(startTime)) return null;
  if (endTime && !TIME_RE.test(endTime)) return null;

  return {
    courtId,
    date,
    startTime,
    durationMinutes: endTime ? durationMinutesFromSlot(startTime, endTime) : 60,
  };
}

export function buildCourtHireBookingSearchParams(
  courtId: string,
  date: string,
  startTime: string,
  endTime: string
): URLSearchParams {
  return new URLSearchParams({
    court: courtId,
    date,
    start: startTime,
    end: endTime,
  });
}

export function normalizeTimeHm(value: string): string {
  const trimmed = value.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return trimmed;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return trimmed;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function timeToMinutes(value: string): number {
  const normalized = normalizeTimeHm(value);
  const [hours, minutes] = normalized.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;
  return hours * 60 + minutes;
}

function minutesToHm(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function intervalsOverlap(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string
): boolean {
  const a0 = timeToMinutes(aStart);
  const a1 = timeToMinutes(aEnd);
  const b0 = timeToMinutes(bStart);
  const b1 = timeToMinutes(bEnd);
  return a0 < b1 && b0 < a1;
}

export interface CourtHireAvailabilitySlot {
  startTime: string;
  endTime: string;
  state: 'available' | 'unavailable' | 'booked';
}

export interface CourtHireAvailabilityCourtRow {
  id: string;
  name: string;
  slots: CourtHireAvailabilitySlot[];
}

export interface CourtHireAvailabilityGrid {
  date: string;
  slotDurationMinutes: number;
  operatingWindow: { startTime: string; endTime: string };
  courts: CourtHireAvailabilityCourtRow[];
}

export interface CourtHireAvailabilityValidation {
  available: boolean;
  reason?: string;
}

export interface CourtHireHeldBookingSelection {
  bookingId: string;
  courtId: string;
  bookingDate: string;
  startTime: string;
  durationMinutes: number;
}

export function matchesCourtHireHeldBooking(
  held: CourtHireHeldBookingSelection | null | undefined,
  params: {
    courtId: string;
    bookingDate: string;
    startTime: string;
    durationMinutes: number;
  }
): boolean {
  if (!held) return false;
  return (
    held.courtId === params.courtId &&
    held.bookingDate === params.bookingDate &&
    normalizeTimeHm(held.startTime) === normalizeTimeHm(params.startTime) &&
    held.durationMinutes === params.durationMinutes
  );
}

export function validateCourtHireBookingAgainstGrid(
  grid: CourtHireAvailabilityGrid,
  courtId: string,
  startTime: string,
  durationMinutes: number,
  heldBooking?: CourtHireHeldBookingSelection | null
): CourtHireAvailabilityValidation {
  const court = grid.courts.find((row) => row.id === courtId);
  if (!court) {
    return { available: false, reason: 'Selected court is not available for hire' };
  }

  const normalizedStart = normalizeTimeHm(startTime);
  const bookingEnd = minutesToHm(timeToMinutes(normalizedStart) + durationMinutes);

  const matchesHeldBooking =
    heldBooking &&
    heldBooking.courtId === courtId &&
    normalizeTimeHm(heldBooking.startTime) === normalizedStart &&
    heldBooking.durationMinutes === durationMinutes;

  if (matchesHeldBooking) {
    return { available: true };
  }

  const blockingSlot = court.slots.find(
    (slot) =>
      slot.state !== 'available' &&
      intervalsOverlap(slot.startTime, slot.endTime, normalizedStart, bookingEnd)
  );
  if (blockingSlot) {
    return {
      available: false,
      reason: 'This court is no longer available at the selected time',
    };
  }

  const coveringSlots = court.slots.filter(
    (slot) =>
      slot.state === 'available' &&
      intervalsOverlap(slot.startTime, slot.endTime, normalizedStart, bookingEnd)
  );
  if (coveringSlots.length === 0) {
    return {
      available: false,
      reason: 'This time is outside available court hours',
    };
  }

  return { available: true };
}

export function resolveInitialCourtId(
  courts: Array<{ id: string }>,
  preferredCourtId?: string | null
): string {
  if (preferredCourtId && courts.some((court) => court.id === preferredCourtId)) {
    return preferredCourtId;
  }
  return courts[0]?.id ?? '';
}
